<template>
  <template v-if="$route.name === 'students.index'">
    <div class="container-fluid py-4" dir="rtl">
      <!-- Filter Controls -->
      <StudentFilter
        :filters="filters"
        :classes="classes"
        @update:filters="updateFilters"
        @apply-filters="applyFilters"
        @reset-filters="resetFilters"
      />

      <!-- Student Table -->
      <StudentTable
        :students="students"
        :loading="isLoading"
        @edit="openEditDialog"
        @delete="openDeleteDialog"
      />
    </div>

    <PaginationControls
      :pagination="pagination"
      @page-changed="handlePageChange"
      @per-page-changed="handlePerPageChange"
    />

    <!-- Dialogs -->
    <DeleteStudentDialog ref="deleteDialog" @delete="handleDeleteStudent" />
  </template>
  <router-view></router-view>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useStore } from "vuex";
import StudentTable from "@/components/features/students/table/StudentTable.vue";
import PaginationControls from "@/components/common/PaginationControls.vue";
import DeleteStudentDialog from "@/components/features/students/dialogs/DeleteStudentDialog.vue";
import StudentFilter from "@/components/features/students/filters/StudentFilter.vue";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
const { t, locale } = useI18n();

const store = useStore();
const toast = useToast();
const isLoading = ref(false);

// State
const students = computed(() => store.state.students.students);
const pagination = computed(() => store.state.students.pagination);
const filters = computed(() => store.state.students.filters);
const classes = computed(() => store.state.classes.classes);

// Dialog refs
const deleteDialog = ref(null);

// Methods
const fetchStudents = async (page = 1, perPage = 10) => {
  isLoading.value = true;
  try {
    await store.dispatch("students/fetchStudents", { page, perPage });
  } catch (error) {
    notifyError(error, "students.load_failed"); 
  } finally {
    isLoading.value = false;
  }
};
function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;
  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }
    if (typeof data.message === 'string') return data.message;
  }
  if (error?.response?.statusText) return error.response.statusText;
  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;
  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden')); // 👈 show Forbidden
  return toast.error(extractApiMessage(error, fallbackKey));
}

// Updated to use mutations instead of actions
const updateFilters = (newFilters) => {
  store.commit("students/SET_FILTERS", newFilters);
};

const applyFilters = () => {
  fetchStudents(1, pagination.value.perPage);
};

const resetFilters = () => {
  store.commit("students/RESET_FILTERS");
  fetchStudents(1, pagination.value.perPage);
};

const handlePageChange = (page) => {
  fetchStudents(page, pagination.value.perPage);
};

const handlePerPageChange = (perPage) => {
  fetchStudents(1, perPage);
};

const openDeleteDialog = (studentId, studentName) => {
  deleteDialog.value.openDialog(studentId, studentName);
};

const handleDeleteStudent = async (studentId) => {
  try {
    const resp = await store.dispatch("students/deleteStudent", studentId);

    // Normalize status (works for axios responses or custom returns)
    const status = resp?.status ?? resp?.data?.status ?? null;

    // If the action returned nothing or a non-2xx status, treat as failure
    if (!resp || (typeof status === 'number' && status >= 400)) {
      if (status === 403) {
        toast.error(t('auth.forbidden', 'Forbidden'));
        return;
      }
      const msg =
        resp?.data?.message ||
        resp?.message ||
        t("students.delete_failed");
      toast.error(msg);
      return;
    }

    // OK: accept 200 or 204 as success
    if (status && ![200, 204].includes(status)) {
      // Any other weird status => fail
      toast.error(t("students.delete_failed"));
      return;
    }

    const successMessage = resp?.data?.message || t("students.delete_success");
    toast.success(successMessage);

    await store.dispatch("students/fetchStudents");
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden'));
    } else {
      toast.error(
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        t("students.delete_failed")
      );
    }
  }
};

// Initial load
onMounted(() => {
  fetchStudents();
  store.dispatch("classes/fetchClasses");
});
</script>
